import { CATEGORY_LABELS, type Status } from '@helpdesk/shared';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Inbox, MessageCircleQuestion, Plus, Star } from 'lucide-react';
import { Link, useSearchParams } from 'react-router';
import { PushNudge } from '../components/AppStatus.tsx';
import { StatusBadge } from '../components/ticket-badges.tsx';
import { Button, ButtonLink } from '../components/ui/button.tsx';
import { Alert, Card, EmptyState, PageHeader, Spinner } from '../components/ui/misc.tsx';
import { api, qs } from '../lib/api.ts';
import { timeAgo } from '../lib/format.ts';
import type { Page, TicketSummary } from '../lib/types.ts';

const TABS: { key: string; label: string; statuses: Status[] | null }[] = [
  { key: 'active', label: 'Active', statuses: ['open', 'in_progress', 'waiting'] },
  { key: 'done', label: 'Resolved', statuses: ['resolved', 'closed'] },
  { key: 'all', label: 'All', statuses: null },
];

export function MyTicketsPage() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.find((t) => t.key === params.get('show')) ?? TABS[0]!;
  const page = Math.max(1, Number(params.get('page')) || 1);

  const query = useQuery({
    queryKey: ['tickets', 'mine', tab.key, page],
    queryFn: () =>
      api.get<Page<TicketSummary>>(
        `/api/tickets${qs({ status: tab.statuses?.join(','), sort: 'updated', page, pageSize: 20 })}`,
      ),
  });

  // Resolved tickets still waiting for a rating, to nudge from any tab.
  const toRate = useQuery({
    queryKey: ['tickets', 'mine', 'to-rate'],
    queryFn: () =>
      api.get<Page<TicketSummary>>(
        `/api/tickets${qs({ status: 'resolved,closed', sort: 'updated', pageSize: 50 })}`,
      ),
    select: (d) => d.items.filter((t) => t.needsRating),
  });

  const setTab = (key: string) => setParams(key === 'active' ? {} : { show: key });
  const setPage = (p: number) =>
    setParams({ ...(tab.key !== 'active' && { show: tab.key }), page: String(p) });

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title="My tickets"
        actions={
          <div className="hidden md:block">
            <ButtonLink to="/new">
              <Plus className="size-4" aria-hidden />
              Report a problem
            </ButtonLink>
          </div>
        }
      />

      <PushNudge text="Get a notification on this device when IT replies or your ticket is resolved." />

      <div
        role="tablist"
        aria-label="Filter tickets"
        className="mb-4 inline-flex rounded-lg bg-surface-2 p-1"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            type="button"
            aria-selected={t.key === tab.key}
            onClick={() => setTab(t.key)}
            className={`rounded-md px-4 py-1.5 text-sm font-medium ${
              t.key === tab.key ? 'bg-surface text-text shadow-card' : 'text-muted hover:text-text'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {!!toRate.data?.length && tab.key !== 'done' && (
        <Alert tone="info" title="How did we do?" className="mb-4">
          {toRate.data.length === 1
            ? 'One of your resolved tickets hasn’t been rated yet.'
            : `${toRate.data.length} of your resolved tickets haven’t been rated yet.`}{' '}
          It takes one tap and helps IT improve.{' '}
          <button type="button" onClick={() => setTab('done')} className="font-medium underline">
            Rate now
          </button>
        </Alert>
      )}
      {query.isPending && <Spinner />}
      {query.isError && <Alert tone="error">{query.error.message}</Alert>}
      {query.data && query.data.items.length === 0 && (
        <Card>
          <EmptyState
            icon={<Inbox className="size-6" />}
            title={tab.key === 'active' ? 'No open tickets' : 'Nothing here yet'}
            action={<ButtonLink to="/new">Report a problem</ButtonLink>}
          >
            {tab.key === 'active'
              ? 'Everything’s working? Great. If something breaks, report it here.'
              : null}
          </EmptyState>
        </Card>
      )}
      {query.data && query.data.items.length > 0 && (
        <>
          <Card className="divide-y divide-line overflow-hidden">
            {query.data.items.map((t) => (
              <TicketRow key={t.id} ticket={t} />
            ))}
          </Card>
          {query.data.pages > 1 && (
            <div className="mt-4 flex items-center justify-between text-sm">
              <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <span className="text-muted">
                Page {page} of {query.data.pages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= query.data.pages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TicketRow({ ticket: t }: { ticket: TicketSummary }) {
  const needsReply = t.status === 'waiting';
  return (
    <Link
      to={`/tickets/${t.number}`}
      className={`flex items-center gap-3 px-4 py-4 transition-colors hover:bg-surface-2 sm:px-5 ${needsReply ? 'bg-violet-soft/50' : ''}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={t.status} forStaff />
          <span className="font-mono text-xs text-muted">{t.number}</span>
        </div>
        <p className="mt-1.5 truncate font-medium">{t.subject}</p>
        <p className="mt-0.5 text-sm text-muted">
          {CATEGORY_LABELS[t.category]} · updated {timeAgo(t.updatedAt)}
        </p>
        {t.needsRating && (
          <p className="mt-1.5 flex items-center gap-1.5 text-sm font-medium text-brand">
            <Star className="size-4" aria-hidden />
            Tap to rate the help you got
          </p>
        )}
        {needsReply && (
          <p className="mt-1.5 flex items-center gap-1.5 text-sm font-medium text-violet">
            <MessageCircleQuestion className="size-4" aria-hidden />
            IT is waiting for your reply
          </p>
        )}
      </div>
      <ChevronRight className="size-5 shrink-0 text-muted" aria-hidden />
    </Link>
  );
}
